import React, { useState } from 'react';
import {
  Database,
  Layers,
  ShieldCheck,
  AlertTriangle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  FileSpreadsheet,
  HardDrive,
  Copy,
  AlertCircle,
  FileCheck2,
  CalendarX,
  UserX,
  Sliders
} from 'lucide-react';
import { useDataLakeQuality, useDataWarehouseQuality } from '../hooks/useKPI';

const Loading: React.FC<{ label?: string }> = ({ label }) => (
  <div className="flex flex-col items-center justify-center min-h-[300px] p-12">
    <div className="w-10 h-10 border-3 border-primary-200 border-t-primary-600 rounded-full animate-spin mb-4" />
    <p className="text-xs font-semibold text-muted-foreground">{label || 'Analyzing dataset metrics...'}</p>
  </div>
);

const ErrorMsg: React.FC<{ msg: string }> = ({ msg }) => (
  <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 flex items-center gap-3">
    <AlertCircle className="w-5 h-5 text-rose-600 shrink-0" />
    <p className="text-xs font-semibold text-rose-800">{msg}</p>
  </div>
);

interface StatCardProps {
  label: string;
  value: string | number;
  sub?: string;
  badge?: string;
  icon?: React.ReactNode;
  variant?: 'default' | 'success' | 'warning' | 'danger' | 'purple';
}

const StatCard: React.FC<StatCardProps> = ({ label, value, sub, icon, variant = 'default' }) => {
  const variantStyles = {
    default: 'bg-card border-border text-foreground',
    success: 'bg-emerald-50/40 border-emerald-200/70 text-emerald-950',
    warning: 'bg-amber-50/40 border-amber-200/70 text-amber-950',
    danger: 'bg-rose-50/40 border-rose-200/70 text-rose-950',
    purple: 'bg-purple-50/40 border-purple-200/70 text-purple-950',
  };

  const iconBg = {
    default: 'bg-primary-50 text-primary-600 border-primary-100',
    success: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    warning: 'bg-amber-100 text-amber-700 border-amber-200',
    danger: 'bg-rose-100 text-rose-700 border-rose-200',
    purple: 'bg-purple-100 text-purple-700 border-purple-200',
  };

  return (
    <div className={`rounded-2xl p-4 border shadow-sm transition-all hover:shadow-md ${variantStyles[variant]}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-[11px] font-bold text-muted-foreground tracking-wide">{label}</span>
        {icon && (
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center border ${iconBg[variant]}`}>
            {icon}
          </div>
        )}
      </div>
      <p className="text-2xl font-black font-mono tracking-tight">{value}</p>
      {sub && <p className="text-[11px] text-muted-foreground mt-1 font-medium">{sub}</p>}
    </div>
  );
};

// ── NA Paginated Table ─────────────────────────────────────────────────────────
const PAGE_SIZE = 6;

const NaPaginatedTable: React.FC<{ naEntries: [string, { missing_count: number; missing_percentage: number }][] }> = ({ naEntries }) => {
  const [page, setPage] = useState(0);
  const totalPages = Math.ceil(naEntries.length / PAGE_SIZE);
  const pageEntries = naEntries.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  return (
    <div className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden">
      <div className="p-4 bg-muted/40 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-amber-500 text-white flex items-center justify-center shadow-sm">
            <AlertTriangle className="w-4 h-4" />
          </div>
          <div>
            <h3 className="text-xs font-bold text-foreground">Missing Values (NA) Breakdown</h3>
            <p className="text-[11px] text-muted-foreground">{naEntries.length} field columns inspected</p>
          </div>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
              className={`px-2.5 py-1 rounded-lg border border-border text-xs font-bold transition flex items-center gap-1 ${
                page === 0 ? 'text-muted-foreground/40 bg-muted/20 cursor-not-allowed' : 'bg-card text-foreground hover:bg-muted cursor-pointer'
              }`}
            >
              <ChevronLeft className="w-3.5 h-3.5" />
              <span>Prev</span>
            </button>
            <span className="text-xs text-muted-foreground font-medium px-2">
              {page + 1} / {totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={page === totalPages - 1}
              className={`px-2.5 py-1 rounded-lg border border-border text-xs font-bold transition flex items-center gap-1 ${
                page === totalPages - 1 ? 'text-muted-foreground/40 bg-muted/20 cursor-not-allowed' : 'bg-card text-foreground hover:bg-muted cursor-pointer'
              }`}
            >
              <span>Next</span>
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-muted/60 border-b border-border text-[11px] font-bold text-muted-foreground uppercase tracking-wider">
              <th className="py-2.5 px-4">Field Attribute</th>
              <th className="py-2.5 px-4 text-right">Missing Records</th>
              <th className="py-2.5 px-4 text-right">Completeness Deficit</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border text-xs">
            {pageEntries.map(([field, val], i) => {
              const pct = val.missing_percentage;
              const color = pct > 80 ? 'bg-rose-500 text-rose-700' : pct > 50 ? 'bg-amber-500 text-amber-700' : pct > 20 ? 'bg-amber-400 text-amber-600' : 'bg-emerald-500 text-emerald-700';
              const textClass = pct > 80 ? 'text-rose-600' : pct > 50 ? 'text-amber-600' : 'text-foreground';

              return (
                <tr key={i} className="hover:bg-muted/40 transition">
                  <td className="py-3 px-4 font-semibold text-foreground font-mono text-[11px]">
                    {field.replace(/_/g, ' ').toUpperCase()}
                  </td>
                  <td className="py-3 px-4 text-right font-mono font-bold text-muted-foreground">
                    {val.missing_count.toLocaleString()}
                  </td>
                  <td className="py-3 px-4 text-right">
                    <div className="flex items-center justify-end gap-3">
                      <div className="w-24 bg-muted h-2 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${color.split(' ')[0]}`}
                          style={{ width: `${Math.min(pct, 100)}%` }}
                        />
                      </div>
                      <span className={`font-mono font-bold text-xs min-w-[48px] ${textClass}`}>
                        {pct.toFixed(1)}%
                      </span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ── Data Lake Tab ─────────────────────────────────────────────────────────────
const DataLakeTab: React.FC = () => {
  const { data, isLoading, error } = useDataLakeQuality();

  if (isLoading) return <Loading label="Evaluating Data Lake hygiene..." />;
  if (error) return <ErrorMsg msg={(error as any).message || 'Failed to load Data Lake metrics'} />;
  if (!data) return <div className="text-center py-12 text-xs text-muted-foreground">No Data Lake records available.</div>;

  const s = data.summary || {};
  const naAnalysis: Record<string, { missing_count: number; missing_percentage: number }> = data.na_analysis || {};
  const naEntries = Object.entries(naAnalysis).sort((a: any, b: any) => b[1].missing_percentage - a[1].missing_percentage);

  const score = s.data_quality_score ?? 0;
  const scoreVariant = score >= 80 ? 'success' : score >= 60 ? 'warning' : 'danger';

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Total Raw Records"
          value={(s.total_records || 0).toLocaleString()}
          icon={<Database className="w-4 h-4" />}
          variant="default"
        />
        <StatCard
          label="Lake Quality Index"
          value={`${score}%`}
          icon={<ShieldCheck className="w-4 h-4" />}
          variant={scoreVariant}
        />
        <StatCard
          label="Overall NA Deficit"
          value={`${s.overall_na_percentage?.toFixed(1) ?? 0}%`}
          icon={<AlertTriangle className="w-4 h-4" />}
          variant={s.overall_na_percentage > 10 ? 'warning' : 'default'}
        />
        <StatCard
          label="Mandatory Missing"
          value={(s.records_missing_mandates || 0).toLocaleString()}
          icon={<FileCheck2 className="w-4 h-4" />}
          variant={s.records_missing_mandates > 0 ? 'danger' : 'success'}
        />
        <StatCard
          label="Missing Gender"
          value={(s.gender_missing_count || 0).toLocaleString()}
          sub="Null gender records"
          icon={<UserX className="w-4 h-4" />}
          variant="default"
        />
        <StatCard
          label="Gender Syntax Noise"
          value={(s.gender_syntax_error_count || 0).toLocaleString()}
          sub="Invalid token values"
          icon={<Sliders className="w-4 h-4" />}
          variant={s.gender_syntax_error_count > 0 ? 'warning' : 'default'}
        />
        <StatCard
          label="Invalid Birth Year"
          value={(s.negative_age_count || 0).toLocaleString()}
          sub="Future or negative ages"
          icon={<CalendarX className="w-4 h-4" />}
          variant={s.negative_age_count > 0 ? 'danger' : 'default'}
        />
        <StatCard
          label="Illogical Records"
          value={(s.records_with_illogical_data || 0).toLocaleString()}
          icon={<AlertCircle className="w-4 h-4" />}
          variant={s.records_with_illogical_data > 0 ? 'danger' : 'success'}
        />
      </div>

      {naEntries.length > 0 && <NaPaginatedTable naEntries={naEntries} />}

      {(!data.mandate_analysis || data.mandate_analysis.length === 0) && (!data.illogic_analysis || data.illogic_analysis.length === 0) && (
        <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-4 flex items-center gap-3 shadow-sm">
          <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
          <div>
            <p className="text-xs font-bold text-emerald-900">Zero Critical Schema Inconsistencies</p>
            <p className="text-[11px] text-emerald-700">No mandatory missing fields or illogical medical constraints detected.</p>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Data Warehouse Tab ────────────────────────────────────────────────────────
const DataWarehouseTab: React.FC = () => {
  const { data, isLoading, error } = useDataWarehouseQuality();

  if (isLoading) return <Loading label="Auditing Data Warehouse schemas..." />;
  if (error) return <ErrorMsg msg={(error as any).message || 'Failed to load Data Warehouse metrics'} />;
  if (!data) return <div className="text-center py-12 text-xs text-muted-foreground">No Data Warehouse records available.</div>;

  const s = data.summary || {};
  const dup = data.duplicate_analysis || {};
  const score = s.warehouse_readiness_score ?? 0;
  const scoreVariant = score >= 80 ? 'success' : score >= 60 ? 'warning' : 'danger';
  const total = s.total_records || 1;
  const cleanCount = total - (dup.exact_duplicate_count || 0) - (s.records_with_consistency_issues || 0) - (s.records_with_format_issues || 0);
  const cleanPct = ((cleanCount / total) * 100).toFixed(1);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Total Records"
          value={s.total_records?.toLocaleString() || '0'}
          icon={<HardDrive className="w-4 h-4" />}
          variant="default"
        />
        <StatCard
          label="Readiness Index"
          value={`${score}%`}
          icon={<ShieldCheck className="w-4 h-4" />}
          variant={scoreVariant}
        />
        <StatCard
          label="Pure Clean Records"
          value={cleanCount.toLocaleString()}
          sub={`${cleanPct}% verified normalized`}
          icon={<CheckCircle2 className="w-4 h-4" />}
          variant="success"
        />
        <StatCard
          label="Exact Duplicates"
          value={(dup.exact_duplicate_count || 0).toLocaleString()}
          sub="100% duplicate field hashes"
          icon={<Copy className="w-4 h-4" />}
          variant={dup.exact_duplicate_count > 0 ? 'danger' : 'default'}
        />
        <StatCard
          label="Near Duplicates"
          value={(dup.near_duplicate_count || 0).toLocaleString()}
          sub="Same member, provider & date"
          icon={<Layers className="w-4 h-4" />}
          variant={dup.near_duplicate_count > 0 ? 'warning' : 'default'}
        />
        <StatCard
          label="Consistency Anomalies"
          value={(s.records_with_consistency_issues || 0).toLocaleString()}
          icon={<AlertTriangle className="w-4 h-4" />}
          variant={s.records_with_consistency_issues > 0 ? 'danger' : 'default'}
        />
        <StatCard
          label="Format Non-Compliant"
          value={(s.records_with_format_issues || 0).toLocaleString()}
          icon={<FileSpreadsheet className="w-4 h-4" />}
          variant={s.records_with_format_issues > 0 ? 'purple' : 'default'}
        />
      </div>

      {/* Duplicate Breakdown Card */}
      <div className="bg-card rounded-2xl border border-border p-5 shadow-sm space-y-4">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-primary-600 text-white flex items-center justify-center shadow-sm">
            <Copy className="w-4 h-4" />
          </div>
          <div>
            <h3 className="text-xs font-bold text-foreground">Duplicate Distribution Breakdown</h3>
            <p className="text-[11px] text-muted-foreground">Detailed hash collision & semantic duplicate profiling</p>
          </div>
        </div>

        <div className="space-y-4 pt-2">
          {[
            { label: 'Exact Duplicate Collisions', value: dup.exact_duplicate_count || 0, color: 'bg-rose-500 text-rose-700' },
            { label: 'Semantic Near-Duplicates (Same Service Context)', value: dup.near_duplicate_count || 0, color: 'bg-amber-500 text-amber-700' },
          ].map(item => {
            const pct = (item.value / total) * 100;
            return (
              <div key={item.label} className="bg-muted/30 p-3.5 rounded-xl border border-border/50">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-foreground">{item.label}</span>
                  <span className="font-mono text-xs font-bold">
                    {item.value.toLocaleString()} <span className="text-muted-foreground font-normal">({pct.toFixed(2)}%)</span>
                  </span>
                </div>
                <div className="w-full bg-muted h-2 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${item.color.split(' ')[0]}`}
                    style={{ width: `${Math.min(pct, 100)}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {s.records_with_consistency_issues === 0 && s.records_with_format_issues === 0 && (
        <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-4 flex items-center gap-3 shadow-sm">
          <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
          <div>
            <p className="text-xs font-bold text-emerald-900">Schema Integrity Verified</p>
            <p className="text-[11px] text-emerald-700">All columns adhere strictly to warehouse schema constraints.</p>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────
const DataQuality: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'lake' | 'warehouse'>('lake');

  return (
    <div className="space-y-6">
      {/* Header Banner */}
      <div className="glass-panel p-6 rounded-2xl border border-border shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-600 to-indigo-700 text-white flex items-center justify-center shadow-md shadow-primary-500/20">
            <ShieldCheck className="w-6 h-6" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-bold text-foreground">Data Quality Sentinel</h2>
              <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                Automated Pipeline
              </span>
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">
              Multi-stage integrity analytics across raw Lake and transformed Warehouse
            </p>
          </div>
        </div>

        {/* Tab Switcher */}
        <div className="flex items-center p-1 bg-muted rounded-xl border border-border">
          <button
            onClick={() => setActiveTab('lake')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold transition cursor-pointer ${
              activeTab === 'lake'
                ? 'bg-card text-primary-600 shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Database className="w-3.5 h-3.5" />
            <span>Data Lake</span>
          </button>
          <button
            onClick={() => setActiveTab('warehouse')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold transition cursor-pointer ${
              activeTab === 'warehouse'
                ? 'bg-card text-indigo-600 shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <HardDrive className="w-3.5 h-3.5" />
            <span>Data Warehouse</span>
          </button>
        </div>
      </div>

      {/* Main Tab Content */}
      <div className="bg-card rounded-2xl border border-border p-6 shadow-sm">
        {activeTab === 'lake' && <DataLakeTab />}
        {activeTab === 'warehouse' && <DataWarehouseTab />}
      </div>
    </div>
  );
};

export default DataQuality;